import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';

import moment from 'moment';

import { enqueueSnackbarError, enqueueSnackbarSuccess, enqueueSnackbarWarning } from 'lib/helpers';
import {
	NfcCardSearch,
	NfcCardSort,
	nfcCardSortFields,
	nfcCardStates,
	isNfcCardExpired,
	isNfcCardId,
	lookupNfcCard,
	setLabelBlocked,
	zNfcCardState,
} from 'lib/models/nfcCards';
import { isWebNfcAvailable } from 'lib/webNfc';

import useNfcCards from 'hooks/useNfcCards';
import useNfcCardLabels from 'hooks/useNfcCardLabels';
import useNfcCardValues from 'hooks/useNfcCardValues';
import useNfcLabelStateCounts from 'hooks/useNfcLabelStateCounts';

import ConfirmationDialog from 'components/ConfirmationDialog';
import NfcCardBatchModal from 'components/NfcCardBatchModal';
import NfcCardProductsModal from 'components/NfcCardProductsModal';
import NfcCardsList from 'components/NfcCardsList';
import NfcReadButton from 'components/NfcReadButton';
import { nfcCardsRowsPerPageOptions } from 'components/NfcCardsList/NfcCardsList';
import ScreenContainer from 'components/containers/ScreenContainer';
import CreateIcon from 'components/icons/CreateIcon';

const DEFAULT_ROWS_PER_PAGE = 25;

/** What "no filter" is worth in a MUI select, which cannot hold undefined. */
const ALL = '';

/** Every sort the listing offers, each field both ways. */
const sortOptions: NfcCardSort[] = nfcCardSortFields.flatMap((field) => [
	{ field, direction: 'desc' as const },
	{ field, direction: 'asc' as const },
]);

/** How a sort is written in the URL and in the sort select: `balance:desc`. */
function sortToParam(sort: NfcCardSort | null): string {
	return sort ? `${sort.field}:${sort.direction}` : ALL;
}

/** A sort read from the URL, or null (newest first) when it is not one of the offered. */
function sortFromParam(param: string | null): NfcCardSort | null {
	return sortOptions.find((option) => sortToParam(option) === param) ?? null;
}

/** Which bulk change of a label is waiting for its confirmation, and how many cards it would change. */
type LabelChange = { label: string; blocked: boolean; count: number };

/**
 * The NFC cards, where the administrator registers cards, finds them and blocks them.
 *
 * Laid out for a phone first, because cards are only read with Web NFC from Chrome for
 * Android. Nobody knows a card's UID, so it is neither typed to register a card nor to find
 * one: cards are registered in batches by tapping them, and a card is found by tapping it,
 * which narrows the listing to it. The UID read only travels in the body of the lookup and
 * is never kept or shown: the panel goes on with the card's id (MUTE-606). Anywhere else the create button explains where to do it,
 * finding by tapping does not show, and the rest of the screen works the same.
 *
 * The listing is narrowed by state, label and responsable (picked among the ones the cards
 * have) and sorted by balance, operations, expiration or responsable. With a label picked,
 * every card of that label can be blocked or unblocked at once, after a confirmation that
 * says how many cards it changes.
 *
 * The tapped card, the filters, the sort and the page live in the URL, which is the only
 * place they are read from, so a reload or a shared link opens the same listing.
 */
export default function NfcCards() {
	const { t } = useTranslation();
	const { stateLabel, sortLabel } = useNfcCardLabels();

	const [nfcAvailable] = useState(isWebNfcAvailable);

	const [searchParams, setSearchParams] = useSearchParams();

	const [selected, setSelected] = useState<string[]>([]);

	const [showLoad, setShowLoad] = useState(false);

	const [showBatch, setShowBatch] = useState(false);

	const [showNfcUnavailable, setShowNfcUnavailable] = useState(false);

	const [labelChange, setLabelChange] = useState<LabelChange | null>(null);

	const [changingLabel, setChangingLabel] = useState(false);

	const { labels, responsables, refetch: refetchValues } = useNfcCardValues();

	// The id of the tapped card. One that is not an id (edited by hand) is no filter rather than a refused listing
	const cardParam = searchParams.get('card') || '';
	const scannedCard = isNfcCardId(cardParam) ? cardParam : null;

	const stateParam = zNfcCardState.safeParse(searchParams.get('state'));
	const state = stateParam.success ? stateParam.data : null;

	const label = searchParams.get('label') || null;

	const responsable = searchParams.get('responsable') || null;

	const sort = sortFromParam(searchParams.get('sort'));

	const page = Math.max(Number(searchParams.get('page')) || 0, 0);

	const rowsPerPageParam = Number(searchParams.get('rowsPerPage'));
	const rowsPerPage = nfcCardsRowsPerPageOptions.includes(rowsPerPageParam) ? rowsPerPageParam : DEFAULT_ROWS_PER_PAGE;

	// The tapped card shows whatever its state, label or responsable, so the filters wait until it is removed
	const cardSearch = useMemo<NfcCardSearch>(
		() =>
			scannedCard
				? { _id: scannedCard, page: 0, rowsPerPage }
				: { state, label, responsable, sort, page, rowsPerPage },
		[scannedCard, state, label, responsable, sort, page, rowsPerPage]
	);

	const { loading, cards, total, failed, refetch } = useNfcCards(cardSearch);

	// The bulk change works on the label as it is, while no single card is tapped
	const bulkLabel = scannedCard ? null : label;

	const { counts, refetch: refetchCounts } = useNfcLabelStateCounts(bulkLabel);

	const updateParams = (next: {
		card?: string | null;
		state?: string;
		label?: string | null;
		responsable?: string | null;
		sort?: NfcCardSort | null;
		page?: number;
		rowsPerPage?: number;
	}) => {
		const values = { card: scannedCard, state: state ?? ALL, label, responsable, sort, page, rowsPerPage, ...next };

		setSearchParams(
			{
				...(values.card ? { card: values.card } : {}),
				...(values.state ? { state: values.state } : {}),
				...(values.label ? { label: values.label } : {}),
				...(values.responsable ? { responsable: values.responsable } : {}),
				...(values.sort ? { sort: sortToParam(values.sort) } : {}),
				page: `${values.page}`,
				rowsPerPage: `${values.rowsPerPage}`,
			},
			{ replace: true }
		);
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		updateParams({ page: newPage });
	};

	const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		updateParams({ rowsPerPage: parseInt(e.target.value, 10), page: 0 });
	};

	// A filter changes which cards can be ticked, so the selection starts over
	const onFilterChange = (next: { state?: string; label?: string | null; responsable?: string | null }) => {
		setSelected([]);
		updateParams({ ...next, page: 0 });
	};

	const onSortChange = (value: string) => {
		updateParams({ sort: sortFromParam(value), page: 0 });
	};

	// The selection is kept: tapping cards one by one and ticking them is a way to pick them
	const onScan = async (scanned: string) => {
		try {
			const card = await lookupNfcCard(scanned);
			if (card) {
				updateParams({ card: card._id, page: 0 });
				// Said as soon as the card is read, not when an action on it is refused (MUTE-622)
				if (isNfcCardExpired(card)) {
					enqueueSnackbarWarning(
						card.expiresAt
							? t('nfc:scannedExpiredOn', { date: moment(card.expiresAt).format('DD/MM/YYYY') })
							: t('nfc:scannedExpired')
					);
				}
			} else {
				enqueueSnackbarError(t('nfc:findNotRegistered'));
			}
		} catch (error) {
			enqueueSnackbarError(error);
		}
	};

	const onClearScan = () => {
		updateParams({ card: null, page: 0 });
	};

	const onToggle = (card: NfcCard) => {
		setSelected((current) =>
			current.includes(card._id) ? current.filter((cardId) => cardId !== card._id) : [...current, card._id]
		);
	};

	const onTogglePage = (pageCards: NfcCard[], select: boolean) => {
		const cardIds = pageCards.map((card) => card._id);
		setSelected((current) =>
			select ? Array.from(new Set([...current, ...cardIds])) : current.filter((cardId) => !cardIds.includes(cardId))
		);
	};

	// A loaded selection is done with: keeping it would invite loading the same cards twice
	const onLoadClose = (loaded: boolean) => {
		setShowLoad(false);
		if (loaded) {
			setSelected([]);
			refetch();
		}
	};

	const onCreate = () => {
		if (nfcAvailable) {
			setShowBatch(true);
		} else {
			setShowNfcUnavailable(true);
		}
	};

	const onBatchClose = (changed: boolean) => {
		setShowBatch(false);
		if (changed) {
			refetch();
			refetchValues();
			refetchCounts();
		}
	};

	const onLabelChangeClose = async (confirmed: boolean) => {
		const change = labelChange;
		setLabelChange(null);

		if (!confirmed || !change) return;

		setChangingLabel(true);
		try {
			const changed = await setLabelBlocked(change.label, change.blocked);
			enqueueSnackbarSuccess(
				change.blocked ? t('nfc:labelBlocked', { count: changed }) : t('nfc:labelUnblocked', { count: changed })
			);
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setChangingLabel(false);

		refetch();
		refetchCounts();
	};

	const valueSelect = (
		id: string,
		title: string,
		value: string | null,
		options: string[],
		onChange: (value: string | null) => void
	) => (
		<FormControl sx={{ minWidth: 160 }}>
			<InputLabel id={id} shrink>
				{title}
			</InputLabel>
			<Select
				labelId={id}
				label={title}
				value={value ?? ALL}
				onChange={(event) => onChange(event.target.value || null)}
				disabled={!!scannedCard}
				displayEmpty
				notched
			>
				<MenuItem value={ALL}>{t('nfc:filterAllFemale')}</MenuItem>
				{/* A value from the URL that no card has any more still shows as picked */}
				{!!value && !options.includes(value) && <MenuItem value={value}>{value}</MenuItem>}
				{options.map((option) => (
					<MenuItem key={option} value={option}>
						{option}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);

	return (
		<ScreenContainer title={t('nfc:nfcCards')}>
			<CreateIcon onCreate={onCreate} disabled={loading} />
			{showLoad && <NfcCardProductsModal cardIds={selected} onClose={onLoadClose} />}
			{showBatch && <NfcCardBatchModal labels={labels} responsables={responsables} onClose={onBatchClose} />}
			{showNfcUnavailable && (
				<ConfirmationDialog
					title={t('nfc:batchTitle')}
					description={
						// Chrome hides NDEFReader outside a secure context, so a phone that opened the
						// panel by its LAN address looks like one without NFC: say what is really wrong
						window.isSecureContext ? t('nfc:batchUnavailable') : t('nfc:batchInsecure')
					}
					hideCancel
					onClose={() => setShowNfcUnavailable(false)}
				/>
			)}
			{!!labelChange && (
				<ConfirmationDialog
					title={labelChange.blocked ? t('nfc:labelBlockTitle') : t('nfc:labelUnblockTitle')}
					description={
						labelChange.blocked
							? t('nfc:labelBlockDescription', { count: labelChange.count, label: labelChange.label })
							: t('nfc:labelUnblockDescription', { count: labelChange.count, label: labelChange.label })
					}
					confirmButton={
						labelChange.blocked
							? t('nfc:labelBlockAll', { count: labelChange.count })
							: t('nfc:labelUnblockAll', { count: labelChange.count })
					}
					onClose={onLabelChangeClose}
				/>
			)}

			<Typography variant="body2" sx={{ paddingBottom: 2 }}>
				{t('nfc:subtitle')}
			</Typography>

			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					flexWrap: 'wrap',
					gap: 2,
					alignItems: { xs: 'stretch', sm: 'center' },
					paddingBottom: 2,
				}}
			>
				{/* Unmounted while a modal is open, which stops its read: a card read there must not also filter the listing */}
				{nfcAvailable && !showBatch && !showLoad && (
					<NfcReadButton label={t('nfc:findByScan')} onUid={onScan} large />
				)}

				<FormControl sx={{ minWidth: 160 }}>
					<InputLabel id="nfc-card-state" shrink>
						{t('nfc:filterState')}
					</InputLabel>
					<Select
						labelId="nfc-card-state"
						label={t('nfc:filterState')}
						value={state ?? ALL}
						onChange={(event) => onFilterChange({ state: event.target.value })}
						disabled={!!scannedCard}
						displayEmpty
						notched
					>
						<MenuItem value={ALL}>{t('nfc:filterAll')}</MenuItem>
						{nfcCardStates.map((option) => (
							<MenuItem key={option} value={option}>
								{stateLabel(option)}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{valueSelect('nfc-card-label', t('nfc:label'), label, labels, (value) => onFilterChange({ label: value }))}

				{valueSelect('nfc-card-responsable', t('nfc:responsable'), responsable, responsables, (value) =>
					onFilterChange({ responsable: value })
				)}

				<FormControl sx={{ minWidth: 200 }}>
					<InputLabel id="nfc-card-sort" shrink>
						{t('nfc:sort')}
					</InputLabel>
					<Select
						labelId="nfc-card-sort"
						label={t('nfc:sort')}
						value={sortToParam(sort)}
						onChange={(event) => onSortChange(event.target.value)}
						disabled={!!scannedCard}
						displayEmpty
						notched
					>
						<MenuItem value={ALL}>{t('nfc:sortNewest')}</MenuItem>
						{sortOptions.map((option) => (
							<MenuItem key={sortToParam(option)} value={sortToParam(option)}>
								{sortLabel(option)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{!!scannedCard && (
				<Box sx={{ paddingBottom: 2 }}>
					<Chip label={t('nfc:scanFilter')} onDelete={onClearScan} color="primary" sx={{ maxWidth: '100%' }} />
				</Box>
			)}

			{!!bulkLabel && (
				<Box sx={{ paddingBottom: 2 }}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: { xs: 'column', sm: 'row' },
							gap: { xs: 1, sm: 2 },
							alignItems: { xs: 'stretch', sm: 'center' },
						}}
					>
						<Button
							variant="outlined"
							color="error"
							size="large"
							sx={{ minHeight: 48 }}
							disabled={!counts || counts.block === 0 || changingLabel}
							onClick={() => counts && setLabelChange({ label: bulkLabel, blocked: true, count: counts.block })}
						>
							{t('nfc:labelBlockAll', { count: counts?.block ?? 0 })}
						</Button>

						<Button
							variant="outlined"
							color="success"
							size="large"
							sx={{ minHeight: 48 }}
							disabled={!counts || counts.unblock === 0 || changingLabel}
							onClick={() =>
								counts && setLabelChange({ label: bulkLabel, blocked: false, count: counts.unblock })
							}
						>
							{t('nfc:labelUnblockAll', { count: counts?.unblock ?? 0 })}
						</Button>
					</Box>
					<Typography variant="caption" component="p" sx={{ marginTop: 1 }}>
						{t('nfc:labelBulkHelp', { label: bulkLabel })}
					</Typography>
				</Box>
			)}

			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					flexWrap: 'wrap',
					gap: { xs: 1, sm: 2 },
					alignItems: { xs: 'stretch', sm: 'center' },
					paddingBottom: 2,
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
					<Typography variant="body2">{t('nfc:selectedCount', { count: selected.length })}</Typography>

					<Button onClick={() => setSelected([])} disabled={selected.length === 0}>
						{t('nfc:clearSelection')}
					</Button>
				</Box>

				<Button
					variant="contained"
					size="large"
					onClick={() => setShowLoad(true)}
					disabled={selected.length === 0}
					sx={{ minHeight: 48 }}
				>
					{t('nfc:loadProducts')}
				</Button>
			</Box>

			{failed && (
				<Alert severity="error" sx={{ marginBottom: 1 }}>
					{t('nfc:failed')}
				</Alert>
			)}

			<NfcCardsList
				data={cards}
				total={total}
				refetch={() => {
					refetch();
					refetchCounts();
				}}
				loading={loading || changingLabel}
				page={scannedCard ? 0 : page}
				rowsPerPage={rowsPerPage}
				handleChangePage={handleChangePage}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				selected={selected}
				onToggle={onToggle}
				onTogglePage={onTogglePage}
				emptyMessage={failed ? null : scannedCard ? t('nfc:findNotRegistered') : t('nfc:empty')}
			/>
		</ScreenContainer>
	);
}
